import { Access } from "@p0security/shared/types/access/index";

// A request names a P0 posture, which P0 lists live through the tailnet's
// connector. It reads `posture <target> [--tailnet] [--device]`: a parameter
// is positional when it's required, and a flag when it's optional.

// With one tailnet installed, a request that names none resolves to it.
const tailnet = {
  description:
    "The tailnet, by its Tailnet ID. Needed only when more than one tailnet is installed.",
  label: "Tailnet",
  optional: true,
  type: "static",
  waitForInput: true,
} as const;

/** What a revoke does to the requestor's connections, after any grant. */
const REVOKE_SENTENCE =
  "When it's revoked, Tailscale cuts new and open connections, Tailscale SSH sessions included, within seconds. An SSH window may freeze rather than close.";

export const TailscaleAccesses = {
  posture: Access({
    delegation: {},
    description:
      "A P0 posture on one of your devices, which the tailnet's policy file grants access on",
    generated: {},
    label: "Posture",
    maturity: "beta",
    parameters: {
      target: {
        description:
          "A P0 posture the tailnet's policy file defines, such as posture:p0_prod_db",
        label: "Target",
        type: "dynamic",
      },
      tailnet,
      device: {
        description:
          "Your device to set the posture on, by name or node ID. Needed only when you have more than one on the tailnet.",
        label: "Device",
        optional: true,
        type: "dynamic",
      },
      // `normalize` sets this to the tailnet the request resolved to, so a
      // request always names its tailnet, whether or not the requestor typed
      // one.
      tailnetId: {
        description: "The tailnet P0 resolved the request to",
        label: "Tailnet ID",
        type: "generated",
      },
      // `normalize` sets this to the DNS name install validation last
      // stored, so requestors and approvers can tell the tailnet by more than
      // its ID. A tailnet with no device of its own has none.
      tailnetDnsName: {
        description: "The tailnet's DNS name, such as tail1234.ts.net",
        label: "Tailnet DNS name",
        optional: true,
        type: "generated",
      },
      // `normalize` sets these from the device and the posture it resolved,
      // so the approver sees which device and posture, and a revoke acts on
      // the same ones.
      deviceId: {
        description: "The node ID of the device P0 sets the posture on",
        label: "Device ID",
        type: "generated",
      },
      posture: {
        description: "The posture the device attribute satisfies",
        label: "Posture",
        type: "generated",
      },
      attribute: {
        description:
          "The device attribute P0 sets to true until the request ends",
        label: "Device attribute",
        type: "generated",
      },
      // The posture is only a name the customer chose. What it opens is in
      // their policy file, so the approver sees the grants that name it or
      // another posture reading the same attribute.
      unlocks: {
        description:
          "The grants and SSH rules in the tailnet's policy file that the posture's device attribute opens, through this posture or another that reads it, as P0 found them when you asked",
        label: "Unlocks",
        type: "generated",
      },
    },
    render: {
      // Typed by hand: inferring it from this access would be circular.
      instructions: ({ permission }: { permission: { device?: string } }) =>
        `P0 set the posture's attribute on your device ${permission.device}. Your access is active on that device until the request ends. ${REVOKE_SENTENCE}`,
      locator: "tailnetId",
      policy: "unlocks",
      resource: "device",
    } as const,
  }),
};

export type TailscaleAccesses = typeof TailscaleAccesses;
export type TailscaleAccessType = keyof TailscaleAccesses;
export type TailscalePostureAccess = TailscaleAccesses["posture"];
